// Web Audio multi-track sync engine for the share-web SPA.
//
// Loads every track in the manifest, decodes via the supplied AudioContext, and
// schedules all sources to start at the same `currentTime + headroom` so the
// mix is sample-accurate even after seek. Per-track gain nodes carry
// mute/solo/volume; the master gain feeds destination.
//
// Solo semantics match the desktop app: if ANY track is soloed, only soloed
// tracks are audible; otherwise each track plays at its own volume unless
// muted. Transitions ramp via `setTargetAtTime(…, 0.01)` to avoid clicks.

import type { ShareManifest } from '@bandway/share-types';
import type { MixInput } from './audio/mix-renderer';

export interface TrackState {
  id: string;
  gain: number; // 0..1
  muted: boolean;
  soloed: boolean;
  pan: number;  // TR-2 — [-1, +1], default 0 (center)
}

export interface LoopSection {
  startSec: number;
  endSec: number;
}

/**
 * Pure-function wrap calculator. Returns `startSec` if `currentTime` has
 * reached or crossed `endSec`, else null. Used by the RAF loop tick when a
 * loop is active. Loop wraps only forward — manual seeks outside the range
 * stay untouched until the next forward crossing of `endSec`.
 */
export function computeLoopWrap(
  currentTime: number,
  section: LoopSection | null | undefined,
): number | null {
  if (!section) return null;
  if (currentTime >= section.endSec) return section.startSec;
  return null;
}

export interface PlayerOpts {
  audioContext: AudioContext;
  manifest: ShareManifest;
  /**
   * Resolves a manifest `track.url` to its bytes. The player does NOT know
   * `cdnBase` — the orchestrator (router/page entry) passes a closure that
   * builds the absolute URL (typically
   * `${cdnBase}/shares/${shareId}/${track.url}`) and runs `fetch().arrayBuffer()`.
   * The player just hands `track.url` to this callback as-is.
   *
   * `signal` is the player's own AbortSignal (audit 5.2): the orchestrator MUST
   * forward it into the underlying `fetch(url, { signal })` so `dispose()` can
   * cancel a song's in-flight stem downloads when the user switches songs. It's
   * optional so simpler callers / tests can ignore it.
   */
  fetchAudio: (url: string, signal?: AbortSignal) => Promise<ArrayBuffer>;
}

export type PlayerEvent =
  | { type: 'load-progress'; trackId: string; current: number; total: number }
  | { type: 'loaded' }
  | { type: 'playing' }
  | { type: 'paused' }
  | { type: 'ended' }
  // Mobile background audio: the audible mix changed (gain/mute/solo/pan/master).
  // The background orchestrator re-renders the offline mix on this signal so the
  // <audio> handoff is pre-armed before the tab is backgrounded.
  | { type: 'mixchanged' };

export interface Player {
  load(): Promise<void>;
  play(): Promise<void>;
  pause(): void;
  seek(sec: number): void;
  getPlayhead(): number;
  setTrackState(id: string, state: Partial<TrackState>): void;
  getTrackStates(): Map<string, TrackState>;
  isPlaying(): boolean;
  isLoaded(): boolean;
  /**
   * Returns the current peak level (0..1) for the track's per-track
   * AnalyserNode, computed as max-abs of byte time-domain data normalized
   * around 128. Returns 0 if the track has no analyser (not yet loaded,
   * unknown id, or AnalyserNode unavailable in the host AudioContext).
   */
  getTrackVuLevel(id: string): number;
  /**
   * TR-2: Set the stereo pan for a track. `panValue` in [-1, +1]; values
   * outside the range are clamped. NaN / non-finite → clamped to 0.
   * No-op if the track has no pannerNode (host lacks createStereoPanner).
   */
  setPan(trackId: string, panValue: number): void;
  /**
   * Set the active loop section. While playing, the player polls
   * `getPlayhead()` each animation frame and wraps back to `startSec`
   * when the playhead reaches or crosses `endSec`. Pass `null` to disable
   * looping. Manual seeks outside [startSec, endSec] remain untouched
   * until the next forward crossing of `endSec`.
   */
  setActiveLoopSection(section: LoopSection | null): void;
  /** Set master gain (0..4). Mirrors desktop B-057 clamp. No-op before load(). */
  setMasterGain(gain: number): void;
  /**
   * Mobile summed-stream model: silence (true) or restore (false) the live Web
   * Audio output WITHOUT changing the mix snapshot. Used when the background
   * <audio> element is the primary audible source (mobile) so the live engine
   * doesn't double up. Master value / per-track gains are preserved for the
   * offline render; un-silencing ramps back to the real master.
   */
  setSilent(silent: boolean): void;
  /**
   * Mobile background audio: snapshot the CURRENT live mix as a plain
   * `MixInput[]` (decoded buffer + solo/mute-resolved gain + pan per track),
   * ready to hand to `renderMix()` (audio/mix-renderer.ts). Gain reuses
   * `audibleGainFor` — the same solo/mute resolution used for the live
   * per-track GainNodes — so the offline render matches what's audible now.
   */
  getMixSnapshot(): MixInput[];
  /** Total song duration in seconds, from the manifest. */
  getDuration(): number;
  subscribe(fn: (event: PlayerEvent) => void): () => void;
  /**
   * Tear the player down (audit 5.2). Aborts any in-flight stem downloads so a
   * song switch doesn't waste bandwidth or let the outgoing song's fetches race
   * the incoming one. One player instance is created per song (main.ts
   * swapSong), so a single AbortController covers this instance's whole lifetime.
   * The AbortError the cancelled fetches reject with is swallowed silently.
   */
  dispose(): void;
}

const RAMP_TIME = 0.01; // 10 ms setTargetAtTime time constant (click-free)
const START_HEADROOM = 0.05; // 50 ms scheduling headroom for sample-accurate start

interface TrackGraph {
  trackId: string;
  buffer: AudioBuffer;
  gainNode: GainNode;
  /**
   * TR-2: StereoPannerNode inserted between gainNode and masterGain for
   * per-track stereo pan. `null` when the host AudioContext doesn't expose
   * `createStereoPanner` (older Safari / test mocks without the stub).
   */
  pannerNode: StereoPannerNode | null;
  /**
   * Per-track AnalyserNode tapped off `gainNode` (post-volume/mute/solo so
   * the meter reflects what's actually audible). `null` when the host
   * AudioContext doesn't expose `createAnalyser` (test mocks).
   */
  analyser: AnalyserNode | null;
  /**
   * Pre-allocated buffer for `getByteTimeDomainData` to avoid per-call
   * allocations in the VU read path. Sized to `analyser.fftSize` (256).
   */
  vuBuffer: Uint8Array | null;
  source: AudioBufferSourceNode | null;
}

export function createPlayer(opts: PlayerOpts): Player {
  const { audioContext: ctx, manifest, fetchAudio } = opts;

  const tracks = new Map<string, TrackGraph>();
  const trackStates = new Map<string, TrackState>();
  const subscribers = new Set<(event: PlayerEvent) => void>();

  // audit 5.2 — one AbortController for this player instance's whole lifetime.
  // Its signal is threaded into every stem fetch (see buildTrack); dispose()
  // aborts it so a song switch cancels the outgoing song's in-flight downloads.
  // A fresh player is built per song (main.ts swapSong), so a single controller
  // (no per-load reset) exactly matches the instance lifecycle.
  const abortController = new AbortController();

  let masterGain: GainNode | null = null;
  // Mobile background audio (S1): the offline mix has no master GainNode, so the
  // background render would ignore master volume. Mirror the clamped master
  // value here and bake it into getMixSnapshot()'s per-input gains.
  let masterGainValue = 1;
  // Mobile summed-stream model: when the <audio> element is the primary audible
  // source (mobile), the live Web Audio engine must be SILENCED to avoid double
  // audio — but its mix snapshot must stay intact so the offline render still
  // reflects real gains. So we mute the master output node WITHOUT touching
  // masterGainValue (which feeds getMixSnapshot). setSilent(true) ramps the
  // output to 0; setMasterGain keeps honouring `silent` while it's engaged.
  let silent = false;
  let loaded = false;
  // Memoized in-flight load promise. `load()` is kicked from TWO call sites
  // (player-view/mount.ts and main.ts) on the same instance; the `loaded` flag
  // alone can't dedupe them because it flips only AFTER the first track decodes
  // (async), leaving a window where both calls pass the guard and spin up two
  // worker pools. Two pools build every track twice and overwrite the `tracks`
  // Map, orphaning already-started sources that pause()/stop() can't reach
  // (B-260624). Returning the same promise to every caller keeps a SINGLE pool.
  let loadPromise: Promise<void> | null = null;
  let playing = false;
  let startTime = 0; // ctx.currentTime when the current play() segment began
  let offsetSec = 0; // playhead offset (seconds into the song)
  let activeLoop: LoopSection | null = null;
  let loopRafHandle: number | null = null;
  // C2 (start-on-first): id of the track whose onended drives 'ended' detection.
  // Always the manifest's first track (the canonical/longest), independent of
  // which track happens to finish loading first.
  const firstTrackId = manifest.tracks[0]?.id;

  function emit(event: PlayerEvent): void {
    for (const fn of subscribers) {
      fn(event);
    }
  }

  function loopTick(): void {
    loopRafHandle = null;
    if (!playing || !activeLoop) return;
    const wrapTo = computeLoopWrap(getPlayhead(), activeLoop);
    if (wrapTo !== null) {
      seek(wrapTo);
    }
    scheduleLoopTick();
  }

  function scheduleLoopTick(): void {
    if (loopRafHandle !== null) return;
    if (typeof requestAnimationFrame === 'function') {
      loopRafHandle = requestAnimationFrame(loopTick);
    }
  }

  function cancelLoopTick(): void {
    if (loopRafHandle !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(loopRafHandle);
    }
    loopRafHandle = null;
  }

  function audibleGainFor(state: TrackState): number {
    // Solo aggregation across all tracks
    let anySoloed = false;
    for (const ts of trackStates.values()) {
      if (ts.soloed) {
        anySoloed = true;
        break;
      }
    }
    if (anySoloed) {
      return state.soloed && !state.muted ? state.gain : 0;
    }
    return state.muted ? 0 : state.gain;
  }

  function applyAllTrackGains(): void {
    const t = ctx.currentTime;
    for (const [id, state] of trackStates) {
      const graph = tracks.get(id);
      if (!graph) continue;
      const target = audibleGainFor(state);
      graph.gainNode.gain.setTargetAtTime(target, t, RAMP_TIME);
    }
  }

  // Create + start a BufferSource for an already-wired track graph, at the
  // given transport position. Extracted from play() so both the bulk-start path
  // (play() over all ready tracks) and the late-join path (a track finishing
  // decode while already playing, C2) schedule sources identically. Web Audio
  // `start(when, offset)` is sample-accurate, so a late track started at the
  // live playhead joins the running mix in perfect sync.
  function startTrackSource(graph: TrackGraph, when: number, offset: number): void {
    const source = ctx.createBufferSource();
    source.buffer = graph.buffer;
    source.connect(graph.gainNode);
    if (graph.trackId === firstTrackId) {
      source.onended = () => {
        // Natural end (not pause): pause() calls .stop() which also fires
        // onended, so guard against double-emission.
        if (!playing) return;
        // Loop wrap at natural buffer end. When `activeLoop.endSec` ≥
        // manifest.duration (e.g. CHORUS section reaching the end of the
        // song), the audio source's natural-end fires before the RAF tick
        // can detect the wrap — RAF runs at ~60Hz while the audio clock
        // is sample-accurate. Detect that here and restart at startSec
        // instead of emitting 'ended'.
        if (activeLoop) {
          seek(activeLoop.startSec);
          return;
        }
        playing = false;
        offsetSec = manifest.duration;
        emit({ type: 'ended' });
      };
    }
    source.start(when, offset);
    graph.source = source;
  }

  // Fetch + decode one track and wire its full audio graph. Extracted from
  // load() so a worker pool can run several concurrently. The per-track graph
  // wiring here is identical to the old serial body — only the iteration
  // strategy changed (B-260622).
  //
  // C2 (start-on-first): after wiring, if the transport is already `playing`,
  // the track is a LATE ARRIVAL — start it immediately at the live playhead so
  // it joins the running mix in sample-accurate sync. If paused, it stays wired
  // only and is picked up by the next play() (its source is created there).
  async function buildTrack(track: ShareManifest['tracks'][number]): Promise<void> {
    // audit 5.2 — hand the player's AbortSignal to the fetch so dispose() can
    // cancel this download mid-flight on a song switch.
    const buf = await fetchAudio(track.url, abortController.signal);
    const decoded = await ctx.decodeAudioData(buf);

    const gainNode = ctx.createGain();

    // TR-2: stereo panner between gain and master. Constant-power law per
    // Web Audio spec (matches desktop engine pan-law.js). Guard for older
    // Safari / test hosts that lack createStereoPanner.
    let pannerNode: StereoPannerNode | null = null;
    if (typeof (ctx as { createStereoPanner?: () => StereoPannerNode }).createStereoPanner === 'function') {
      pannerNode = ctx.createStereoPanner();
      pannerNode.pan.value = typeof track.initialPan === 'number' ? track.initialPan : 0;
    }

    const state: TrackState = {
      id: track.id,
      gain: track.initialVolume,
      muted: track.initialMuted,
      soloed: track.initialSoloed,
      pan: typeof track.initialPan === 'number' ? track.initialPan : 0,
    };
    trackStates.set(track.id, state);

    gainNode.gain.value = 0; // will ramp up at play()

    // Routing: gain → panner → master (if panner) ; else gain → master
    if (pannerNode) {
      gainNode.connect(pannerNode);
      pannerNode.connect(masterGain!);
    } else {
      gainNode.connect(masterGain!);
    }

    // Tap a per-track AnalyserNode off gainNode for the VU meter. Sits
    // *after* the gain node so the meter reflects actual audible level
    // (post mute/solo/volume). Peak is pre-pan (analyser on gainNode, not
    // pannerNode) — pan affects routing/position, not level metering.
    // Connects to nothing further — analysers pull samples from anything
    // routed through them, no extra audio path needed. Some test hosts
    // don't expose `createAnalyser`; skip gracefully so the player still
    // works in those mocks.
    let analyser: AnalyserNode | null = null;
    let vuBuffer: Uint8Array | null = null;
    if (typeof (ctx as { createAnalyser?: () => AnalyserNode }).createAnalyser === 'function') {
      analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      gainNode.connect(analyser);
      vuBuffer = new Uint8Array(analyser.fftSize);
    }

    const graph: TrackGraph = {
      trackId: track.id,
      buffer: decoded,
      gainNode,
      pannerNode,                    // TR-2
      analyser,
      vuBuffer,
      source: null,
    };
    tracks.set(track.id, graph);

    // C2 late-join: if we're already playing when this track finishes decoding,
    // start it RIGHT NOW at the current playhead. `startTrackSource` uses
    // `start(when, offset)` (sample-accurate) so the late stem lines up with the
    // already-running sources to the sample. `applyAllTrackGains()` then ramps
    // its gain from 0 up to its audible level (click-free). When paused, do
    // nothing here — the next play() creates this track's source like any other.
    if (playing) {
      // Offset passed to start(when, offset) MUST be >= 0 (Web Audio throws
      // `RangeError: offset value should be positive` otherwise — B-260622).
      // Right after play() the bulk sources are scheduled at `startTime`
      // (= ctx.currentTime + START_HEADROOM) but not yet sounding, so during
      // that pre-roll window ctx.currentTime < startTime and getPlayhead() is
      // negative. Join at the SAME scheduled anchor as the bulk start
      // (startTime/offsetSec) — identical timing, non-negative offset. Once
      // playback is underway (ctx.currentTime >= startTime), getPlayhead() is
      // >= offsetSec >= 0, so join at the live playhead.
      if (ctx.currentTime < startTime) {
        startTrackSource(graph, startTime, offsetSec);
      } else {
        startTrackSource(graph, ctx.currentTime, getPlayhead());
      }
      applyAllTrackGains();
    }

    // S3: the audible mix now includes this stem. Signal 'mixchanged' so the
    // background orchestrator re-renders as stems arrive under start-on-first
    // (its debounce coalesces the initial-load burst into one render).
    emit({ type: 'mixchanged' });
  }

  function load(): Promise<void> {
    // Idempotent against concurrent/repeat calls — share one execution (B-260624).
    if (loadPromise) return loadPromise;
    // On failure, clear the memo so a later call can retry (original behaviour:
    // a rejected load left `loaded` false, so a fresh load() would re-run).
    // Concurrent callers still share THIS rejecting promise.
    loadPromise = doLoad().catch((err) => {
      loadPromise = null;
      throw err;
    });
    return loadPromise;
  }

  async function doLoad(): Promise<void> {
    if (loaded) return;
    masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(ctx.destination);

    const total = manifest.tracks.length;

    if (total === 0) {
      // Degenerate manifest: nothing to load — become playable immediately.
      loaded = true;
      emit({ type: 'loaded' });
      return;
    }

    // Bounded-concurrency worker pool (B-260622). The old serial loop awaited
    // fetch+decode per track before starting the next, so an N-track song was
    // N sequential network round-trips — very slow and, on mobile, prone to
    // OOM. Fetch+decode+wire up to MAX_CONCURRENCY tracks at once instead.
    // Cap of 6 keeps several requests in flight without saturating mobile
    // bandwidth or thrashing the decoder with too many simultaneous decodes.
    // Track order is now non-deterministic, so load-progress reports a
    // COMPLETED counter (incremented as each track finishes), not the index.
    const MAX_CONCURRENCY = 6;
    let nextIndex = 0;
    let completed = 0;

    // C2 (start-on-first): playback becomes available as soon as the FIRST
    // track is fetched+decoded+wired — NOT after the last. `firstReady` is the
    // gate the returned promise awaits; the pool keeps running DETACHED in the
    // background, with each later track wiring itself (and, if we're already
    // playing, late-joining in sync — see buildTrack). Background-track errors
    // are caught/logged so a single bad stem leaves that track silent without
    // rejecting the page; only a FIRST-track failure surfaces the error path.
    let resolveFirst!: () => void;
    let rejectFirst!: (err: unknown) => void;
    const firstReady = new Promise<void>((res, rej) => { resolveFirst = res; rejectFirst = rej; });
    let firstSettled = false;

    async function worker(): Promise<void> {
      while (nextIndex < manifest.tracks.length) {
        const track = manifest.tracks[nextIndex++]!;
        try {
          await buildTrack(track);
        } catch (err) {
          // audit 5.2 — an intentional cancel (dispose() → abort() on song
          // switch) rejects the in-flight fetch with an AbortError. Swallow it
          // silently: no error screen (do NOT rejectFirst), no warn. `return`
          // (not `continue`) stops this worker — the player is being torn down,
          // so there's nothing left to load. Key off the signal (we KNOW we
          // cancelled) with the documented `err.name === 'AbortError'` fallback.
          if (abortController.signal.aborted || (err as { name?: string })?.name === 'AbortError') {
            return;
          }
          if (track.id === firstTrackId && !firstSettled) {
            // First/canonical track failed → surface via the load() promise so
            // the boot 'load-failed'/'offline' error path fires.
            firstSettled = true;
            rejectFirst(err);
          } else {
            // A later stem failed — leave it silent, keep playback going.
            // eslint-disable-next-line no-console
            console.warn(`[player] background track "${track.id}" failed to load`, err);
          }
          continue;
        }
        completed += 1;
        emit({ type: 'load-progress', trackId: track.id, current: completed, total });
        if (!firstSettled) {
          // First track wired → flip `loaded`, emit 'loaded', unblock play().
          // We become playable on the FIRST completion regardless of order:
          // play() starts whatever is ready and the rest join via buildTrack.
          firstSettled = true;
          loaded = true;
          applyAllTrackGains();
          emit({ type: 'loaded' });
          resolveFirst();
        }
      }
    }

    const poolSize = Math.min(MAX_CONCURRENCY, manifest.tracks.length);
    // Run the pool DETACHED — do not await it. The returned promise resolves on
    // `firstReady`. Attach a catch so a fully-failed background pool never
    // produces an unhandled rejection.
    void Promise.all(Array.from({ length: poolSize }, () => worker())).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[player] background track pool error', err);
    });

    await firstReady;
  }

  async function play(): Promise<void> {
    // C2 (start-on-first): `loaded` flips after the FIRST track is ready, not
    // the last, so play() is allowed once any track exists. Guard only that we
    // have at least one wired track — the rest join via buildTrack as they
    // finish decoding (late-join in sync).
    if (!loaded || tracks.size === 0) {
      throw new Error('player: play() called before the first track loaded');
    }
    if (playing) return;

    // Safari/iOS needs an explicit resume on the user gesture. B-104: an
    // AudioContext created outside a gesture (main.ts creates it at page-load)
    // stays 'suspended' on iOS, and per spec resume() resolves only "when the
    // context is running again" — on iOS WebKit that promise can hang. AWAITING
    // it froze play() before source.start(), so on iPhone the Play button did
    // nothing (only Play was dead — non-audio controls don't touch the ctx).
    // Kick resume() WITHOUT awaiting and schedule the sources synchronously, in
    // the gesture call stack — that is the iOS-correct unlock and the sources
    // start as soon as the context transitions to running.
    if (ctx.state !== 'running') {
      void ctx.resume();
    }

    const when = ctx.currentTime + START_HEADROOM;

    // Start every CURRENTLY-READY track at the common anchor. Iterate `tracks`
    // (the wired set) rather than `manifest.tracks` — under start-on-first not
    // every manifest track is decoded yet, and the missing ones late-join via
    // buildTrack at the SAME transport anchor (startTime/offsetSec) captured
    // here. `startTime` MUST be set before startTrackSource so a late track
    // racing in computes getPlayhead() against the right baseline.
    startTime = when;
    for (const graph of tracks.values()) {
      startTrackSource(graph, when, offsetSec);
    }

    playing = true;
    applyAllTrackGains();
    scheduleLoopTick();
    emit({ type: 'playing' });
  }

  function pause(): void {
    if (!playing) return;

    const elapsed = Math.max(0, ctx.currentTime - startTime);
    offsetSec = Math.min(manifest.duration, offsetSec + elapsed);

    playing = false;
    cancelLoopTick();

    for (const graph of tracks.values()) {
      if (graph.source) {
        graph.source.onended = null;
        try {
          graph.source.stop();
        } catch {
          // already stopped — ignore
        }
        graph.source = null;
      }
    }

    emit({ type: 'paused' });
  }

  function seek(sec: number): void {
    const clamped = Math.max(0, Math.min(manifest.duration, sec));
    if (playing) {
      pause();
      offsetSec = clamped;
      // Fire-and-forget — play() only awaits ctx.resume() which is a no-op
      // when already running (Safari already resumed earlier).
      void play();
    } else {
      offsetSec = clamped;
    }
  }

  function getPlayhead(): number {
    if (playing) {
      return ctx.currentTime - startTime + offsetSec;
    }
    return offsetSec;
  }

  function setTrackState(id: string, partial: Partial<TrackState>): void {
    const current = trackStates.get(id);
    if (!current) return;
    const next: TrackState = {
      ...current,
      ...partial,
      id: current.id, // id is immutable
    };
    trackStates.set(id, next);
    // Solo state changes affect every track's audible gain, so reapply globally.
    applyAllTrackGains();
    emit({ type: 'mixchanged' });
  }

  function getTrackStates(): Map<string, TrackState> {
    return trackStates;
  }

  function isPlaying(): boolean {
    return playing;
  }

  function isLoaded(): boolean {
    return loaded;
  }

  function getTrackVuLevel(id: string): number {
    const graph = tracks.get(id);
    if (!graph || !graph.analyser || !graph.vuBuffer) return 0;
    graph.analyser.getByteTimeDomainData(graph.vuBuffer);
    let max = 0;
    for (let i = 0; i < graph.vuBuffer.length; i++) {
      // Time-domain bytes are unsigned, centered at 128. Normalize to
      // [-1, 1] then take abs → 0..1 envelope.
      const norm = Math.abs((graph.vuBuffer[i]! - 128) / 128);
      if (norm > max) max = norm;
    }
    return max;
  }

  function subscribe(fn: (event: PlayerEvent) => void): () => void {
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }

  function setActiveLoopSection(section: LoopSection | null): void {
    activeLoop = section;
    if (playing && section) {
      scheduleLoopTick();
    } else if (!section) {
      cancelLoopTick();
    }
  }

  function setMasterGain(gain: number): void {
    if (!masterGain) return;
    const clamped = Math.min(4, Math.max(0, Number.isFinite(gain) ? gain : 1));
    // S1: keep the mirrored value in sync BEFORE ramping so getMixSnapshot()
    // (and thus the background render + its content key) reflects master.
    masterGainValue = clamped;
    // While silenced (mobile <audio> primary), the live output stays at 0 — but
    // masterGainValue is updated above so getMixSnapshot() and the background
    // render still reflect the new master, and un-silencing restores it.
    masterGain.gain.setTargetAtTime(silent ? 0 : clamped, ctx.currentTime, RAMP_TIME);
    emit({ type: 'mixchanged' });
  }

  // Mobile summed-stream model: silence (or restore) the live Web Audio output
  // without disturbing masterGainValue, so the offline mix snapshot is unchanged
  // and un-silencing ramps back to the real master. No 'mixchanged' emit — the
  // audible mix content did NOT change, only whether the live engine is muted;
  // emitting would trigger a needless background re-render.
  function setSilent(next: boolean): void {
    silent = next;
    if (!masterGain) return;
    masterGain.gain.setTargetAtTime(silent ? 0 : masterGainValue, ctx.currentTime, RAMP_TIME);
  }

  // TR-2: per-track stereo pan via StereoPannerNode.
  function setPan(trackId: string, panValue: number): void {
    const state = trackStates.get(trackId);
    const graph = tracks.get(trackId);
    if (!state || !graph || !graph.pannerNode) return;
    const clamped = !Number.isFinite(panValue) ? 0
                  : panValue < -1 ? -1
                  : panValue > 1  ?  1
                  : panValue;
    state.pan = clamped;
    graph.pannerNode.pan.setTargetAtTime(clamped, ctx.currentTime, RAMP_TIME);
    emit({ type: 'mixchanged' });
  }

  // Mobile background audio: snapshot the live mix for offline rendering
  // (mix-renderer.ts renderMix()). Reuses audibleGainFor so solo/mute
  // resolution isn't duplicated between the live graph and the offline one.
  function getMixSnapshot(): MixInput[] {
    const snapshot: MixInput[] = [];
    for (const [id, graph] of tracks) {
      const state = trackStates.get(id)!;
      snapshot.push({
        buffer: graph.buffer,
        // S1: bake master into the per-input gain so the offline render matches
        // what's audible now (the live path has a master GainNode; the offline
        // one doesn't). This also makes master changes move the content key.
        gain: audibleGainFor(state) * masterGainValue,
        pan: state.pan,
      });
    }
    return snapshot;
  }

  function getDuration(): number {
    return manifest.duration;
  }

  // audit 5.2 — cancel this player's in-flight stem downloads. Called from
  // main.ts disposeSong() on a song switch (and on full unmount). The aborted
  // fetches reject with AbortError, which the load worker swallows silently.
  // Audio-graph teardown is handled by main.ts closing the AudioContext.
  function dispose(): void {
    abortController.abort();
  }

  return {
    load,
    play,
    pause,
    seek,
    getPlayhead,
    setTrackState,
    getTrackStates,
    isPlaying,
    isLoaded,
    getTrackVuLevel,
    setPan,          // TR-2
    setActiveLoopSection,
    setMasterGain,
    setSilent,
    getMixSnapshot,
    getDuration,
    subscribe,
    dispose,
  };
}
